/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaTimes, FaInfoCircle, FaImages, FaImage, FaGlobe, FaQuestionCircle } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ConfirmModal from '../common/ConfirmModal';
import './Categories.css';
import { validateImageFile } from '../utils/fileValidation';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ header: [1, 2, 3, false] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['clean']
  ]
};

const quillFormats = ['bold', 'italic', 'underline', 'header', 'size', 'list', 'bullet', 'align', 'clean'];

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} [AP]M$/.test(dateString)) {
    return dateString;
  }
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');

    return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
  } catch (e) {
    return dateString;
  }
};

const Categories = () => {
  const backendUrl = (
    process.env.REACT_APP_BACKEND ||
    (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) ||
    'http://localhost:8000'
  ).replace(/\/+$/, '');
  const cleanHost = backendUrl;

  // Navigation & View Mode states
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [activeEditTab, setActiveEditTab] = useState('basic'); // 'basic' | 'banner' | 'highlights' | 'widebanner' | 'seo'

  // General Listing states
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('1');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, activeTab]);

  const [showCreateDropdown, setShowCreateDropdown] = useState(false);

  // Modal controls for creation & delete
  const [showMainModal, setShowMainModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryToEdit, setCategoryToEdit] = useState(null);

  // Basic Create Main Category Form Fields
  const [mainTitle, setMainTitle] = useState('');
  const [mainDescription, setMainDescription] = useState('');

  // Basic Create Sub Category Form Fields
  const [subParentId, setSubParentId] = useState('');
  const [subCode, setSubCode] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [subDescription, setSubDescription] = useState('');
  const [subShowProducts, setSubShowProducts] = useState(true);

  // Edit Mode Category Form Fields
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [editCategoryType, setEditCategoryType] = useState('main'); // 'main' | 'sub'

  // Tab 1: Basic Info Edit Fields
  const [editParentId, setEditParentId] = useState('');
  const [editCategoryCode, setEditCategoryCode] = useState('');
  const [editCategoryTitle, setEditCategoryTitle] = useState('');
  const [editCategoryDescription, setEditCategoryDescription] = useState('');
  const [editLandingUrl, setEditLandingUrl] = useState('');
  const [editShowProducts, setEditShowProducts] = useState(true);
  const [basicStatus, setBasicStatus] = useState(true);

  // Tab 2: Banner Configuration Edit Fields (Multiple Slides Support)
  const [bannerSlides, setBannerSlides] = useState([
    { id: '0', type: 'image', image: '', video: '', alt: '', heading: '', description: '', thumb: 1, status: 1, sort: '1' }
  ]);
  const [bannerStatus, setBannerStatus] = useState(true);

  // Helper functions for Banner Slides
  const handleAddBannerSlide = () => {
    setBannerSlides((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        type: 'image',
        image: '',
        video: '',
        alt: '',
        heading: '',
        description: '',
        thumb: prev.length === 0 ? 1 : 0,
        status: 1,
        sort: String(prev.length + 1)
      }
    ]);
  };

  const handleRemoveBannerSlide = (index) => {
    if (bannerSlides.length <= 1) {
      toast.warning('At least one slide is required.');
      return;
    }
    setBannerSlides((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      const hasThumb = updated.some((s) => s.thumb === 1 || s.thumb === '1');
      if (!hasThumb && updated.length > 0) {
        updated[0].thumb = 1;
      }
      return updated;
    });
  };

  const handleUpdateBannerSlide = (index, field, value) => {
    setBannerSlides((prev) => {
      const updated = [...prev];
      if (field === 'thumb') {
        return updated.map((slide, i) => ({
          ...slide,
          thumb: i === index ? 1 : 0
        }));
      }
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSlideFileUpload = async (e, index) => {
    if (!validateImageFile(e.target.files?.[0], e)) return;
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      toast.info('Uploading file...');
      const res = await axiosInstance.post('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleUpdateBannerSlide(index, 'image', res.data.filename);
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('File upload failed.');
    }
  };

  // Tab 3: Category Highlights Edit Fields
  const [highlightImage, setHighlightImage] = useState('');
  const [highlightHeading, setHighlightHeading] = useState('');
  const [highlightDescription, setHighlightDescription] = useState('');
  const [highlightAlt, setHighlightAlt] = useState('');
  const [highlightStatus, setHighlightStatus] = useState(true);

  // Tab 4: Wide Banner Widget Edit Fields
  const [wideBannerImage, setWideBannerImage] = useState('');
  const [wideBannerHeading, setWideBannerHeading] = useState('');
  const [wideBannerDescription, setWideBannerDescription] = useState('');
  const [wideBannerAlt, setWideBannerAlt] = useState('');
  const [wideBannerButton, setWideBannerButton] = useState('');
  const [wideBannerUrl, setWideBannerUrl] = useState('');
  const [wideBannerStatus, setWideBannerStatus] = useState(true);

  // Tab 5: FAQ Manager Edit Fields
  const [faqList, setFaqList] = useState([]);
  const [faqStatus, setFaqStatus] = useState(true);

  // Tab 6: SEO Manager Edit Fields
  const [seoMetaTitle, setSeoMetaTitle] = useState('');
  const [seoMetaDescription, setSeoMetaDescription] = useState('');
  const [seoMetaKeywords, setSeoMetaKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [seoStatus, setSeoStatus] = useState(true);

  const createDropdownRef = useRef(null);

  // Tag inputs helper
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = keywordInput.trim().replace(/,$/, '');
      if (val && !seoMetaKeywords.includes(val)) {
        setSeoMetaKeywords([...seoMetaKeywords, val]);
      }
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setSeoMetaKeywords(seoMetaKeywords.filter((_, idx) => idx !== indexToRemove));
  };

  // FAQ Manager helpers
  const handleAddFaq = () => {
    setFaqList((prev) => [...prev, { id: String(Date.now()), question: '', answer: '' }]);
  };

  const handleRemoveFaq = (indexToRemove) => {
    setFaqList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleUpdateFaq = (index, field, value) => {
    setFaqList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(error.response?.data?.message || 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();

    const handleClickOutside = (event) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setShowCreateDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const mainCategoriesList = categories.filter((cat) => cat.type === 'main' && cat.status);

  // Search filter
  const filteredCategories = categories.filter((cat) => {
    const matchesTab = activeTab === 'all' || (activeTab === 'main' && cat.type === 'main') || (activeTab === 'sub' && cat.type === 'sub');

    const matchesSearch =
      cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (cat.code && cat.code.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus =
      filterStatus === '' || (filterStatus === '1' && cat.status === true) || (filterStatus === '3' && cat.status === false);

    return matchesTab && matchesSearch && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

  // Toggle Category Status
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const response = await axiosInstance.put(`/categories/${id}`, { status: !currentStatus });
      setCategories((prev) => prev.map((cat) => (cat.id === id ? { ...cat, status: response.data.status } : cat)));
      toast.success(`Category is now ${!currentStatus ? 'Active' : 'Inactive'}`);
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(error.response?.data?.message || 'Failed to toggle status.');
    }
  };

  // Submit new Main Category
  const handleCreateMain = async (e) => {
    e.preventDefault();
    const cleanText = mainDescription.replace(/<[^>]*>?/gm, '').trim();
    if (!cleanText) {
      toast.error('Description is required.');
      return;
    }

    try {
      const payload = {
        title: mainTitle,
        description: mainDescription,
        type: 'main'
      };
      await axiosInstance.post('/categories', payload);
      toast.success('Main Category created successfully!');
      setShowMainModal(false);
      setMainTitle('');
      setMainDescription('');
      fetchCategories();
    } catch (error) {
      console.error('Error creating main category:', error);
      toast.error(error.response?.data?.message || 'Failed to create Main Category.');
    }
  };

  // Submit new Sub Category
  const handleCreateSub = async (e) => {
    e.preventDefault();
    if (!subParentId) {
      toast.error('Please select a parent category.');
      return;
    }
    const cleanText = subDescription.replace(/<[^>]*>?/gm, '').trim();
    if (!cleanText) {
      toast.error('Description is required.');
      return;
    }

    try {
      const payload = {
        title: subTitle,
        description: subDescription,
        code: subCode,
        type: 'sub',
        parentId: parseInt(subParentId),
        showProducts: subShowProducts
      };
      await axiosInstance.post('/categories', payload);
      toast.success('Sub Category created successfully!');
      setShowSubModal(false);
      setSubParentId('');
      setSubCode('');
      setSubTitle('');
      setSubDescription('');
      setSubShowProducts(true);
      fetchCategories();
    } catch (error) {
      console.error('Error creating sub category:', error);
      toast.error(error.response?.data?.message || 'Failed to create Sub Category.');
    }
  };

  // File Uploader helper
  const handleFileUpload = async (e, setFileName) => {
    if (!validateImageFile(e.target.files?.[0], e)) return;
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      toast.info('Uploading file...');
      const res = await axiosInstance.post('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFileName(res.data.filename);
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('File upload failed.');
    }
  };

  // Open Edit Tabbed view and fetch full details
  const openEditView = async (category) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/categories/${category.id}`);
      const data = res.data;

      setEditCategoryId(data.id);
      setEditCategoryType(data.type);
      setEditCategoryTitle(data.title || '');
      setEditCategoryDescription((data.description || '').replace(/<br\s*\/?>/g, '\n'));
      setEditParentId(data.parentId || '');
      setEditCategoryCode(data.code || '');
      setEditShowProducts(data.showProducts);
      setEditLandingUrl(data.landingUrl || '');
      setBasicStatus(data.status === true || data.status === 1);

      const sections = data.sectionContents || [];

      // Section 1: Banner Configuration
      const sec1 = sections.find((s) => s.section_id === 1);
      if (sec1 && sec1.content) {
        try {
          const parsed = typeof sec1.content === 'string' ? JSON.parse(sec1.content) : sec1.content;
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Find explicitly selected thumbnail slide (look for thumb === 1)
            let selectedThumbIndex = -1;
            for (let i = 0; i < parsed.length; i++) {
              if (parsed[i].thumb === 1 || parsed[i].thumb === '1' || parsed[i].thumb === true) {
                selectedThumbIndex = i;
                break;
              }
            }
            if (selectedThumbIndex === -1) selectedThumbIndex = 0;

            const formattedSlides = parsed.map((slide, idx) => ({
              id: String(slide.id || idx),
              type: slide.type || 'image',
              image: slide.image || '',
              video: slide.video || '',
              alt: slide.alt || '',
              heading: slide.heading || '',
              description: slide.description || '',
              thumb: idx === selectedThumbIndex ? 1 : 0,
              status: slide.status === 0 || slide.status === '0' ? 0 : 1,
              sort: String(slide.sort || idx + 1)
            }));
            setBannerSlides(formattedSlides);
          } else if (parsed && typeof parsed === 'object') {
            setBannerSlides([
              {
                id: '0',
                type: parsed.type || 'image',
                image: parsed.image || parsed.banner || '',
                video: parsed.video || '',
                alt: parsed.alt || '',
                heading: parsed.heading || '',
                description: parsed.description || '',
                thumb: 1,
                status: 1,
                sort: '1'
              }
            ]);
          }
          setBannerStatus(sec1.status === true || sec1.status === 1 || sec1.status === '1');
        } catch (e) {
          console.error(e);
          setBannerSlides([
            { id: '0', type: 'image', image: '', video: '', alt: '', heading: '', description: '', thumb: 1, status: 1, sort: '1' }
          ]);
          setBannerStatus(true);
        }
      } else {
        setBannerSlides([
          { id: '0', type: 'image', image: '', video: '', alt: '', heading: '', description: '', thumb: 1, status: 1, sort: '1' }
        ]);
        setBannerStatus(true);
      }

      // Section 2: Highlights
      const sec2 = sections.find((s) => s.section_id === 2);
      if (sec2 && sec2.content) {
        try {
          const parsed = typeof sec2.content === 'string' ? JSON.parse(sec2.content) : sec2.content;
          setHighlightHeading(parsed.heading || '');
          setHighlightDescription(parsed.description || '');
          setHighlightImage(parsed.image || '');
          setHighlightAlt(parsed.alt || '');
          setHighlightStatus(sec2.status);
        } catch (e) {
          console.error(e);
        }
      } else {
        setHighlightHeading('');
        setHighlightDescription('');
        setHighlightImage('');
        setHighlightAlt('');
        setHighlightStatus(true);
      }

      // Section 3: Wide Banner Widget
      const sec3 = sections.find((s) => s.section_id === 3);
      if (sec3 && sec3.content) {
        try {
          const parsed = typeof sec3.content === 'string' ? JSON.parse(sec3.content) : sec3.content;
          setWideBannerHeading(parsed.heading || '');
          setWideBannerDescription(parsed.description || '');
          setWideBannerImage(parsed.image || '');
          setWideBannerAlt(parsed.alt || '');
          setWideBannerButton(parsed.button || '');
          setWideBannerUrl(parsed.url || '');
          setWideBannerStatus(sec3.status);
        } catch (e) {
          console.error(e);
        }
      } else {
        setWideBannerHeading('Classical');
        setWideBannerDescription('Designs that will stand and have stood the test of time. Simply timeless. Classics');
        setWideBannerImage('wide-banner-image.png');
        setWideBannerAlt('wide-banner-image');
        setWideBannerButton('View Collections');
        setWideBannerUrl('https://www.aristo-india.com/');
        setWideBannerStatus(true);
      }

      // Section 4: SEO Manager
      const sec4 = sections.find((s) => s.section_id === 4);
      if (sec4 && sec4.content) {
        try {
          const parsed = typeof sec4.content === 'string' ? JSON.parse(sec4.content) : sec4.content;
          setSeoMetaTitle(parsed.meta_title || '');
          setSeoMetaDescription(parsed.meta_description || '');
          const kwArray = parsed.meta_keywords
            ? parsed.meta_keywords
                .split(',')
                .map((k) => k.trim())
                .filter(Boolean)
            : [];
          setSeoMetaKeywords(kwArray);
          setSeoStatus(sec4.status);
        } catch (e) {
          console.error(e);
        }
      } else {
        setSeoMetaTitle('');
        setSeoMetaDescription('');
        setSeoMetaKeywords([]);
        setSeoStatus(true);
      }

      // Section 5: FAQ Manager
      const sec5 = sections.find((s) => Number(s.section_id) === 5);
      if (sec5 && sec5.content) {
        try {
          const parsed = typeof sec5.content === 'string' ? JSON.parse(sec5.content) : sec5.content;
          if (Array.isArray(parsed)) {
            setFaqList(
              parsed.map((item, idx) => ({
                id: item.id || String(Date.now() + idx),
                question: item.question || item.q || '',
                answer: item.answer || item.a || ''
              }))
            );
          } else if (parsed && typeof parsed === 'object') {
            const list = Array.isArray(parsed.faqs) ? parsed.faqs : [];
            setFaqList(
              list.map((item, idx) => ({
                id: item.id || String(Date.now() + idx),
                question: item.question || item.q || '',
                answer: item.answer || item.a || ''
              }))
            );
          } else {
            setFaqList([]);
          }
          setFaqStatus(sec5.status === true || sec5.status === 1 || sec5.status === '1');
        } catch (e) {
          console.error(e);
          setFaqList([]);
          setFaqStatus(true);
        }
      } else {
        setFaqList([]);
        setFaqStatus(true);
      }

      setActiveEditTab('basic');
      setView('edit');
    } catch (err) {
      console.error(err);
      toast.error('Failed to load category detailed information.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEdit = () => {
    if (categoryToEdit) {
      const target = categoryToEdit;
      setCategoryToEdit(null);
      openEditView(target);
    }
  };

  // Submit full edits
  const handleSaveEditCategory = async (e) => {
    e.preventDefault();

    const cleanText = editCategoryDescription ? editCategoryDescription.replace(/<[^>]*>?/gm, '').trim() : '';
    if (!cleanText) {
      toast.error('Basic Info: Description is required.');
      return;
    }

    const htmlDesc = editCategoryDescription;

    try {
      setLoading(true);

      // Ensure strictly one slide is saved as thumbnail
      let thumbIdx = bannerSlides.findIndex((s) => s.thumb === 1 || s.thumb === '1' || s.thumb === true);
      if (thumbIdx === -1) thumbIdx = 0;

      const sectionContents = [
        {
          section_id: 1,
          status: bannerStatus,
          content: bannerSlides.map((slide, idx) => ({
            id: String(slide.id || idx),
            type: slide.type || 'image',
            image: slide.image || '',
            video: slide.video || '',
            alt: slide.alt || '',
            heading: slide.heading || '',
            description: slide.description || '',
            thumb: idx === thumbIdx ? 1 : 0,
            status: slide.status === 0 || slide.status === '0' ? 0 : 1,
            sort: String(idx + 1)
          }))
        },
        {
          section_id: 2,
          status: highlightStatus,
          content: {
            heading: highlightHeading,
            description: highlightDescription,
            image: highlightImage,
            alt: highlightAlt,
            status: highlightStatus ? 1 : 0
          }
        },
        {
          section_id: 3,
          status: wideBannerStatus,
          content: {
            heading: wideBannerHeading,
            description: wideBannerDescription,
            image: wideBannerImage,
            alt: wideBannerAlt,
            button: wideBannerButton,
            url: wideBannerUrl,
            status: wideBannerStatus ? 1 : 0
          }
        },
        {
          section_id: 4,
          status: seoStatus,
          content: {
            meta_title: seoMetaTitle,
            meta_description: seoMetaDescription,
            meta_keywords: seoMetaKeywords.join(','),
            meta_tags: ''
          }
        },
        {
          section_id: 5,
          status: faqStatus,
          content: {
            status: faqStatus ? 1 : 0,
            faqs: faqList
              .filter((f) => f.question && f.question.trim())
              .map((f) => ({
                id: f.id,
                question: f.question.trim(),
                answer: (f.answer || '').trim()
              }))
          }
        }
      ];

      const payload = {
        title: editCategoryTitle.trim(),
        description: htmlDesc,
        code: editCategoryType === 'sub' ? editCategoryCode.trim().toUpperCase() : null,
        parentId: editCategoryType === 'sub' ? parseInt(editParentId) : null,
        showProducts: editCategoryType === 'sub' ? editShowProducts : false,
        status: basicStatus,
        landingUrl: editLandingUrl.trim() || null,
        sectionContents
      };

      await axiosInstance.put(`/categories/${editCategoryId}`, payload);
      toast.success('Category updated successfully!');
      setView('list');
      fetchCategories();
    } catch (error) {
      console.error('Error modifying category:', error);
      toast.error(error.response?.data?.message || 'Failed to update category.');
    } finally {
      setLoading(false);
    }
  };

  // Delete Category
  const handleDeleteCategory = async () => {
    try {
      const token =
        localStorage.getItem('token') ||
        sessionStorage.getItem('token') ||
        localStorage.getItem('userToken') ||
        sessionStorage.getItem('userToken');

      await axiosInstance.delete(`/categories/${categoryToDelete.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Category deleted successfully!');
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.message || 'Failed to delete category.');
    }
  };

  const handleOpenMainModal = (e) => {
    e.stopPropagation();
    setShowMainModal(true);
    setShowCreateDropdown(false);
  };

  const handleOpenSubModal = (e) => {
    e.stopPropagation();
    setShowSubModal(true);
    setShowCreateDropdown(false);
  };

  return (
    <>
      <div className="premium-page-container animate-fade-up">
        {view === 'list' ? (
          <>
            {/* Page Header */}
            <div className="premium-page-header">
              <h2 className="page-title">Products Category Manager</h2>

              <div className="create-btn-container" ref={createDropdownRef}>
                <button
                  className="btn-create-dropdown"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateDropdown(!showCreateDropdown);
                  }}
                >
                  <FaPlus size={14} />
                  <span>Add Category</span>
                </button>

                {showCreateDropdown && (
                  <div className="create-dropdown-menu">
                    <button type="button" className="create-dropdown-item" onClick={handleOpenMainModal}>
                      Main Category
                    </button>
                    <button type="button" className="create-dropdown-item" onClick={handleOpenSubModal}>
                      Sub Category
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Control Bar (Filters, Search) */}
            <div className="premium-controls-bar">
              <div className="tabs-group">
                <button className={`tab-pill ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
                  All Category
                </button>
                <button className={`tab-pill ${activeTab === 'main' ? 'active' : ''}`} onClick={() => setActiveTab('main')}>
                  Main Category
                </button>
                <button className={`tab-pill ${activeTab === 'sub' ? 'active' : ''}`} onClick={() => setActiveTab('sub')}>
                  Sub Category
                </button>
              </div>

              <select className="premium-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="1">Active Categories</option>
                <option value="3">Inactive Categories</option>
                <option value="">All Categories</option>
              </select>

              <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search here..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Categories Table */}
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                <div className="premium-table-wrapper">
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th style={{ width: '4%' }}>
                          <input type="checkbox" className="premium-checkbox" />
                        </th>
                        <th style={{ width: '12%' }}>Status</th>
                        <th style={{ width: '13%' }}>Type</th>
                        <th style={{ width: '8%' }}>Code</th>
                        <th style={{ width: '17%' }}>Category Title</th>
                        <th style={{ width: '20%' }}>Description</th>
                        <th style={{ width: '16%' }}>Created On</th>
                        <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.length === 0 ? (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', padding: '4rem', color: 'rgba(245, 240, 232, 0.4)' }}>
                            No categories found.
                          </td>
                        </tr>
                      ) : (
                        currentCategories.map((category) => (
                          <tr key={category.id}>
                            <td>
                              <input type="checkbox" className="premium-checkbox" />
                            </td>
                            <td>
                              <div className="switch-container">
                                <label className="switch" htmlFor={`status-toggle-${category.id}`}>
                                  <span
                                    style={{
                                      position: 'absolute',
                                      width: '1px',
                                      height: '1px',
                                      padding: 0,
                                      margin: '-1px',
                                      overflow: 'hidden',
                                      clip: 'rect(0, 0, 0, 0)',
                                      border: 0
                                    }}
                                  >
                                    Toggle Status
                                  </span>
                                  <input
                                    id={`status-toggle-${category.id}`}
                                    type="checkbox"
                                    checked={category.status}
                                    onChange={() => handleToggleStatus(category.id, category.status)}
                                  />
                                  <span className="slider"></span>
                                </label>
                                <span className="switch-label">{category.status ? 'Active' : 'Inactive'}</span>
                              </div>
                            </td>
                            <td className="nowrap-cell">
                              <span className={`badge-type ${category.type}`}>
                                {category.type === 'main' ? 'Main Category' : 'Sub Category'}
                              </span>
                            </td>
                            <td>{category.code || '—'}</td>
                            <td>
                              <div className="category-name-cell">
                                <span className="category-title-text">{category.title}</span>
                                {category.type === 'sub' && category.parent && (
                                  <span className="category-parent-text">{category.parent.title}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="text-truncate-cell" title={category.description}>
                                {category.description}
                              </div>
                            </td>
                            <td className="nowrap-cell">{formatDateTime(category.createdAt)}</td>
                            <td style={{ textAlign: 'center' }}>
                              <div className="inline-actions-container" style={{ justifyContent: 'center' }}>
                                <button
                                  type="button"
                                  className="btn-inline-action edit-action"
                                  title="Modify"
                                  onClick={() => setCategoryToEdit(category)}
                                >
                                  <FaEdit size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-inline-action delete-action"
                                  title="Delete"
                                  onClick={() => {
                                    setCategoryToDelete(category);
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  <FaTrash size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {filteredCategories.length > 0 && (
                  <div className="premium-pagination-container">
                    <div className="premium-pagination-info">
                      Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredCategories.length)}</span> of{' '}
                      <span>{filteredCategories.length}</span> entries
                    </div>
                    <div className="premium-pagination-list">
                      <button className="premium-pagination-btn arrow-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                        &laquo;
                      </button>
                      <button
                        className="premium-pagination-btn arrow-btn"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        &lsaquo;
                      </button>

                      {Array.from({ length: Math.ceil(filteredCategories.length / itemsPerPage) }, (_, idx) => idx + 1)
                        .filter((page) => {
                          const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
                          return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                        })
                        .map((page, index, array) => {
                          return (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && (
                                <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                              )}
                              <button
                                className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}

                      <button
                        className="premium-pagination-btn arrow-btn"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredCategories.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(filteredCategories.length / itemsPerPage)}
                      >
                        &rsaquo;
                      </button>
                      <button
                        className="premium-pagination-btn arrow-btn"
                        onClick={() => setCurrentPage(Math.ceil(filteredCategories.length / itemsPerPage))}
                        disabled={currentPage === Math.ceil(filteredCategories.length / itemsPerPage)}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          /* PREMIUM TABBED EDITOR VIEW */
          <form onSubmit={handleSaveEditCategory} className="category-editor-form">
            <div className="categories-header">
              <h2 className="categories-title">Products Category Manager</h2>
            </div>

            <div className="category-editor-container">
              {/* Sidebar Tabs */}
              <div className="category-editor-sidebar">
                <div
                  role="button"
                  tabIndex={0}
                  className={`category-tab-btn ${activeEditTab === 'basic' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('basic')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setActiveEditTab('basic');
                  }}
                >
                  <div className="category-tab-btn-content">
                    <FaInfoCircle size={16} />
                    <span>Basic Info</span>
                  </div>
                  <div className="category-tab-status">
                    <label className="tab-mini-switch" htmlFor="sidebarBasicStatus" title={basicStatus ? 'Active' : 'Inactive'}>
                      <span
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          border: 0
                        }}
                      >
                        Toggle Basic Status
                      </span>
                      <input
                        id="sidebarBasicStatus"
                        type="checkbox"
                        checked={basicStatus}
                        onChange={(e) => setBasicStatus(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tab-mini-slider"></span>
                    </label>
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  className={`category-tab-btn ${activeEditTab === 'banner' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('banner')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setActiveEditTab('banner');
                  }}
                >
                  <div className="category-tab-btn-content">
                    <FaImages size={16} />
                    <span>Banner Configuration</span>
                  </div>
                  <div className="category-tab-status">
                    <label className="tab-mini-switch" htmlFor="sidebarBannerStatus" title={bannerStatus ? 'Active' : 'Inactive'}>
                      <span
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          border: 0
                        }}
                      >
                        Toggle Banner Status
                      </span>
                      <input
                        id="sidebarBannerStatus"
                        type="checkbox"
                        checked={bannerStatus}
                        onChange={(e) => setBannerStatus(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tab-mini-slider"></span>
                    </label>
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  className={`category-tab-btn ${activeEditTab === 'widebanner' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('widebanner')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setActiveEditTab('widebanner');
                  }}
                >
                  <div className="category-tab-btn-content">
                    <FaImage size={16} />
                    <span>Wide Banner Widget</span>
                  </div>
                  <div className="category-tab-status">
                    <label className="tab-mini-switch" htmlFor="sidebarWideBannerStatus" title={wideBannerStatus ? 'Active' : 'Inactive'}>
                      <span
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          border: 0
                        }}
                      >
                        Toggle Wide Banner Status
                      </span>
                      <input
                        id="sidebarWideBannerStatus"
                        type="checkbox"
                        checked={wideBannerStatus}
                        onChange={(e) => setWideBannerStatus(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tab-mini-slider"></span>
                    </label>
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  className={`category-tab-btn ${activeEditTab === 'faq' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('faq')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setActiveEditTab('faq');
                  }}
                >
                  <div className="category-tab-btn-content">
                    <FaQuestionCircle size={16} />
                    <span>FAQ Manager</span>
                  </div>
                  <div className="category-tab-status">
                    <label className="tab-mini-switch" htmlFor="sidebarFaqStatus" title={faqStatus ? 'Active' : 'Inactive'}>
                      <span
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          border: 0
                        }}
                      >
                        Toggle FAQ Status
                      </span>
                      <input
                        id="sidebarFaqStatus"
                        type="checkbox"
                        checked={faqStatus}
                        onChange={(e) => setFaqStatus(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tab-mini-slider"></span>
                    </label>
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  className={`category-tab-btn ${activeEditTab === 'seo' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('seo')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setActiveEditTab('seo');
                  }}
                >
                  <div className="category-tab-btn-content">
                    <FaGlobe size={16} />
                    <span>SEO Manager</span>
                  </div>
                  <div className="category-tab-status">
                    <label className="tab-mini-switch" htmlFor="sidebarSeoStatus" title={seoStatus ? 'Active' : 'Inactive'}>
                      <span
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          border: 0
                        }}
                      >
                        Toggle SEO Status
                      </span>
                      <input
                        id="sidebarSeoStatus"
                        type="checkbox"
                        checked={seoStatus}
                        onChange={(e) => setSeoStatus(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tab-mini-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Tab Panel Content */}
              <div className="category-editor-content">
                {activeEditTab === 'basic' && (
                  <div className="category-editor-pane animate-scale-up">
                    <div className="category-editor-pane-header">
                      <h3 className="category-editor-pane-title">Basic Info</h3>
                      <div className="switch-container">
                        <label className="switch" htmlFor="basicStatusToggle">
                          <span
                            style={{
                              position: 'absolute',
                              width: '1px',
                              height: '1px',
                              padding: 0,
                              margin: '-1px',
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              border: 0
                            }}
                          >
                            Toggle Category Status
                          </span>
                          <input
                            id="basicStatusToggle"
                            type="checkbox"
                            checked={basicStatus}
                            onChange={(e) => setBasicStatus(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">{basicStatus ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>

                    {editCategoryType === 'sub' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                        <div className="premium-form-group">
                          <label htmlFor="editParentId">
                            Category <span>*</span>
                          </label>
                          <select
                            id="editParentId"
                            className="premium-select"
                            required
                            value={editParentId}
                            onChange={(e) => setEditParentId(e.target.value)}
                          >
                            <option value="">Select Category</option>
                            {mainCategoriesList.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.title}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="premium-form-group">
                          <label htmlFor="editCategoryCode">
                            Category Code <span>*</span>
                          </label>
                          <input
                            type="text"
                            id="editCategoryCode"
                            className="premium-input"
                            required
                            value={editCategoryCode}
                            onChange={(e) => setEditCategoryCode(e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    <div className="premium-form-group">
                      <label htmlFor="editCategoryTitle">
                        Category Title <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="editCategoryTitle"
                        className="premium-input"
                        required
                        value={editCategoryTitle}
                        onChange={(e) => setEditCategoryTitle(e.target.value)}
                      />
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="editCategoryDescription">
                        Description <span>*</span>
                      </label>
                      <div className="quill-editor-wrapper">
                        <ReactQuill
                          id="editCategoryDescription"
                          theme="snow"
                          value={editCategoryDescription}
                          onChange={setEditCategoryDescription}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Write category description..."
                        />
                      </div>
                    </div>

                    <div className="premium-form-group" style={{ marginBottom: '1.5rem' }}>
                      <label htmlFor="editLandingUrl">
                        Associated Landing Page URL <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="editLandingUrl"
                        className="premium-input"
                        placeholder="https://example.com/category-designs/"
                        value={editLandingUrl}
                        onChange={(e) => setEditLandingUrl(e.target.value)}
                      />
                    </div>

                    {editCategoryType === 'sub' && (
                      <div className="premium-form-group">
                        <span
                          className="premium-label-title"
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'rgba(245, 240, 232, 0.75)',
                            marginBottom: '8px',
                            letterSpacing: '0.3px'
                          }}
                        >
                          Show products under category listing page?
                        </span>
                        <div className="radio-group">
                          <label className="radio-option" htmlFor="editShowProductsYes">
                            <input
                              id="editShowProductsYes"
                              type="radio"
                              name="editShowProducts"
                              checked={editShowProducts === true}
                              onChange={() => setEditShowProducts(true)}
                            />
                            <span>Yes</span>
                          </label>
                          <label className="radio-option" htmlFor="editShowProductsNo">
                            <input
                              id="editShowProductsNo"
                              type="radio"
                              name="editShowProducts"
                              checked={editShowProducts === false}
                              onChange={() => setEditShowProducts(false)}
                            />
                            <span>No</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeEditTab === 'banner' && (
                  <div className="category-editor-pane animate-scale-up">
                    <div className="category-editor-pane-header">
                      <h3 className="category-editor-pane-title">Banner Configuration</h3>
                      <div className="switch-container">
                        <label className="switch" htmlFor="bannerStatusToggle">
                          <span
                            style={{
                              position: 'absolute',
                              width: '1px',
                              height: '1px',
                              padding: 0,
                              margin: '-1px',
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              border: 0
                            }}
                          >
                            Toggle Banner Status
                          </span>
                          <input
                            id="bannerStatusToggle"
                            type="checkbox"
                            checked={bannerStatus}
                            onChange={(e) => setBannerStatus(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">{bannerStatus ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>

                    {bannerSlides.map((slide, index) => (
                      <div key={slide.id || index} className="slide-card">
                        <div className="slide-card-header">
                          <span className="slide-card-title">Slide {index + 1}</span>
                          <div className="slide-card-actions">
                            <div className="switch-container">
                              <label className="switch" htmlFor={`slideStatus-${index}`}>
                                <span
                                  style={{
                                    position: 'absolute',
                                    width: '1px',
                                    height: '1px',
                                    padding: 0,
                                    margin: '-1px',
                                    overflow: 'hidden',
                                    clip: 'rect(0, 0, 0, 0)',
                                    border: 0
                                  }}
                                >
                                  Toggle Slide Status
                                </span>
                                <input
                                  id={`slideStatus-${index}`}
                                  type="checkbox"
                                  checked={slide.status === 1 || slide.status === '1'}
                                  onChange={(e) => handleUpdateBannerSlide(index, 'status', e.target.checked ? 1 : 0)}
                                />
                                <span className="slider"></span>
                              </label>
                              <span className="switch-label">{slide.status === 1 || slide.status === '1' ? 'Active' : 'Inactive'}</span>
                            </div>
                            {bannerSlides.length > 1 && (
                              <button
                                type="button"
                                className="btn-slide-delete"
                                title="Remove Slide"
                                onClick={() => handleRemoveBannerSlide(index)}
                              >
                                <FaTrash size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="premium-form-group">
                          <label htmlFor={`bannerType-${index}`}>
                            Banner Type <span>*</span>
                          </label>
                          <select
                            id={`bannerType-${index}`}
                            className="premium-select"
                            value={slide.type || 'image'}
                            onChange={(e) => handleUpdateBannerSlide(index, 'type', e.target.value)}
                          >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </select>
                        </div>

                        <div className="premium-upload-grid">
                          <div className="upload-preview-container">
                            {slide.image ? (
                              <img src={`${cleanHost}/uploads/${slide.image}`} alt="Banner Preview" className="upload-preview-image" />
                            ) : (
                              <div className="upload-preview-placeholder">
                                <FaImages size={24} />
                                <span>No image chosen</span>
                              </div>
                            )}
                          </div>
                          <div className="upload-controls">
                            <span
                              className="form-label"
                              style={{
                                fontSize: '13px',
                                fontWeight: '550',
                                color: 'rgba(245, 240, 232, 0.85)',
                                marginBottom: '4px',
                                display: 'block'
                              }}
                            >
                              Banner Image *
                            </span>
                            <div className="file-input-wrapper">
                              <button type="button" className="btn-file-select">
                                Choose file
                              </button>
                              <input type="file" accept="image/*" onChange={(e) => handleSlideFileUpload(e, index)} />
                            </div>
                            <span className="upload-file-info">{slide.image || 'No file chosen'}</span>
                          </div>
                        </div>

                        <div className="premium-form-group" style={{ marginBottom: '1.25rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                            <input
                              id={`setAsThumbnailCheckbox-${index}`}
                              type="radio"
                              name="categoryThumbnailRadio"
                              checked={slide.thumb === 1 || slide.thumb === '1'}
                              onChange={() => handleUpdateBannerSlide(index, 'thumb', 1)}
                              style={{ margin: 0, width: '16px', height: '16px', accentColor: '#c4a882', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(245, 240, 232, 0.75)', margin: 0 }}>
                              Set as Thumbnail
                            </span>
                          </label>
                        </div>

                        <div className="premium-form-group">
                          <label htmlFor={`bannerAlt-${index}`}>Alt Text</label>
                          <input
                            type="text"
                            id={`bannerAlt-${index}`}
                            className="premium-input"
                            placeholder="Enter Alt Text"
                            value={slide.alt || ''}
                            onChange={(e) => handleUpdateBannerSlide(index, 'alt', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}

                    <button type="button" className="btn-add-slide-dashed" onClick={handleAddBannerSlide}>
                      <FaPlus size={14} />
                      <span>Add New Slide</span>
                    </button>
                  </div>
                )}

                {activeEditTab === 'widebanner' && (
                  <div className="category-editor-pane animate-scale-up">
                    <div className="category-editor-pane-header">
                      <h3 className="category-editor-pane-title">Wide Banner Widget</h3>
                      <div className="switch-container">
                        <label className="switch" htmlFor="wideBannerStatusToggle">
                          <span
                            style={{
                              position: 'absolute',
                              width: '1px',
                              height: '1px',
                              padding: 0,
                              margin: '-1px',
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              border: 0
                            }}
                          >
                            Toggle Wide Banner Status
                          </span>
                          <input
                            id="wideBannerStatusToggle"
                            type="checkbox"
                            checked={wideBannerStatus}
                            onChange={(e) => setWideBannerStatus(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">{wideBannerStatus ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>

                    <div className="premium-upload-grid">
                      <div className="upload-preview-container">
                        {wideBannerImage ? (
                          <img src={`${cleanHost}/uploads/${wideBannerImage}`} alt="Wide Banner Preview" className="upload-preview-image" />
                        ) : (
                          <div className="upload-preview-placeholder">
                            <FaImage size={24} />
                            <span>No image chosen</span>
                          </div>
                        )}
                      </div>
                      <div className="upload-controls">
                        <span
                          className="form-label"
                          style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: 'rgba(245, 240, 232, 0.85)',
                            marginBottom: '8px',
                            display: 'block'
                          }}
                        >
                          Banner Image (Optional)
                        </span>
                        <div className="file-input-wrapper">
                          <button type="button" className="btn-file-select">
                            Choose file
                          </button>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, setWideBannerImage)} />
                        </div>
                        <span className="upload-file-info">{wideBannerImage || 'No file chosen'}</span>
                      </div>
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="wideBannerHeading">Banner Title (Optional)</label>
                      <input
                        type="text"
                        id="wideBannerHeading"
                        className="premium-input"
                        placeholder="Enter Banner Title"
                        value={wideBannerHeading}
                        onChange={(e) => setWideBannerHeading(e.target.value)}
                      />
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="wideBannerDescription">Banner Description (Optional)</label>
                      <textarea
                        id="wideBannerDescription"
                        className="premium-textarea"
                        placeholder="Enter Banner Description"
                        value={wideBannerDescription}
                        onChange={(e) => setWideBannerDescription(e.target.value)}
                      />
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="wideBannerAlt">Alt Text (Optional)</label>
                      <input
                        type="text"
                        id="wideBannerAlt"
                        className="premium-input"
                        placeholder="Enter Alt Text"
                        value={wideBannerAlt}
                        onChange={(e) => setWideBannerAlt(e.target.value)}
                      />
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="wideBannerButton">Banner Button Text (Optional)</label>
                      <input
                        type="text"
                        id="wideBannerButton"
                        className="premium-input"
                        placeholder="e.g. View Collections"
                        value={wideBannerButton}
                        onChange={(e) => setWideBannerButton(e.target.value)}
                      />
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="wideBannerUrl">Banner URL (Optional)</label>
                      <input
                        type="text"
                        id="wideBannerUrl"
                        className="premium-input"
                        placeholder="https://example.com/wide-banner-target/"
                        value={wideBannerUrl}
                        onChange={(e) => setWideBannerUrl(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {activeEditTab === 'faq' && (
                  <div className="category-editor-pane animate-scale-up">
                    <div className="category-editor-pane-header">
                      <h3 className="category-editor-pane-title">FAQ Manager</h3>
                      <div className="switch-container">
                        <label className="switch" htmlFor="faqStatusToggle">
                          <span
                            style={{
                              position: 'absolute',
                              width: '1px',
                              height: '1px',
                              padding: 0,
                              margin: '-1px',
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              border: 0
                            }}
                          >
                            Toggle FAQ Status
                          </span>
                          <input
                            id="faqStatusToggle"
                            type="checkbox"
                            checked={faqStatus}
                            onChange={(e) => setFaqStatus(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">{faqStatus ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>

                    {!faqStatus ? (
                      <div
                        className="oops-disabled-overlay animate-fade-up"
                        style={{
                          padding: '2rem 1rem',
                          textAlign: 'center',
                          background: 'rgba(255,255,255,0.02)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.05)'
                        }}
                      >
                        <p style={{ color: '#94a3b8', margin: 0 }}>
                          FAQ Section is disabled for this category. Toggle the switch above to activate FAQs.
                        </p>
                      </div>
                    ) : (
                      <div className="category-faq-editor-list" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {faqList.length === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              padding: '2rem',
                              background: 'rgba(255,255,255,0.02)',
                              borderRadius: '8px',
                              border: '1px dashed rgba(196,168,130,0.2)'
                            }}
                          >
                            <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>No FAQs added for this category yet.</p>
                            <button
                              type="button"
                              className="btn-premium-primary"
                              onClick={handleAddFaq}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                            >
                              <FaPlus size={12} /> Add First FAQ
                            </button>
                          </div>
                        ) : (
                          <>
                            {faqList.map((faq, idx) => (
                              <div
                                key={faq.id || idx}
                                className="category-faq-card"
                                style={{
                                  background: 'rgba(255,255,255,0.03)',
                                  border: '1px solid rgba(196,168,130,0.25)',
                                  borderRadius: '8px',
                                  padding: '1.25rem'
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1rem',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    paddingBottom: '0.5rem'
                                  }}
                                >
                                  <span style={{ color: '#c4a882', fontWeight: 600, fontSize: '0.9rem' }}>FAQ #{idx + 1}</span>
                                  <button
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.35rem',
                                      fontSize: '0.85rem'
                                    }}
                                    onClick={() => handleRemoveFaq(idx)}
                                  >
                                    <FaTrash size={12} /> Remove
                                  </button>
                                </div>

                                <div className="premium-form-group" style={{ marginBottom: '1rem' }}>
                                  <label htmlFor={`faq_question_${idx}`}>
                                    Question <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <input
                                    id={`faq_question_${idx}`}
                                    type="text"
                                    className="premium-input"
                                    placeholder="e.g. What customization options are available for this range?"
                                    value={faq.question}
                                    onChange={(e) => handleUpdateFaq(idx, 'question', e.target.value)}
                                  />
                                </div>

                                <div className="premium-form-group" style={{ marginBottom: 0 }}>
                                  <label htmlFor={`faq_answer_${idx}`}>
                                    Answer <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <textarea
                                    id={`faq_answer_${idx}`}
                                    className="premium-textarea"
                                    rows={3}
                                    placeholder="Enter detailed answer here..."
                                    value={faq.answer}
                                    onChange={(e) => handleUpdateFaq(idx, 'answer', e.target.value)}
                                  />
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              className="btn-premium-secondary"
                              style={{
                                border: '1px dashed #c4a882',
                                color: '#c4a882',
                                padding: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem'
                              }}
                              onClick={handleAddFaq}
                            >
                              <FaPlus size={12} /> Add Another FAQ
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeEditTab === 'seo' && (
                  <div className="category-editor-pane animate-scale-up">
                    <div className="category-editor-pane-header">
                      <h3 className="category-editor-pane-title">SEO Manager</h3>
                      <div className="switch-container">
                        <label className="switch" htmlFor="seoStatusToggle">
                          <span
                            style={{
                              position: 'absolute',
                              width: '1px',
                              height: '1px',
                              padding: 0,
                              margin: '-1px',
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              border: 0
                            }}
                          >
                            Toggle SEO Status
                          </span>
                          <input
                            id="seoStatusToggle"
                            type="checkbox"
                            checked={seoStatus}
                            onChange={(e) => setSeoStatus(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">{seoStatus ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="seoMetaTitle">Meta Title (Optional)</label>
                      <input
                        type="text"
                        id="seoMetaTitle"
                        className="premium-input"
                        placeholder="Enter Meta Title"
                        value={seoMetaTitle}
                        onChange={(e) => setSeoMetaTitle(e.target.value)}
                      />
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="seoMetaDescription">Meta Description (Optional)</label>
                      <div className="quill-editor-wrapper">
                        <ReactQuill
                          id="seoMetaDescription"
                          theme="snow"
                          value={seoMetaDescription}
                          onChange={setSeoMetaDescription}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Enter Meta Description..."
                        />
                      </div>
                    </div>

                    <div className="premium-form-group">
                      <label htmlFor="seoMetaKeywords">Meta Keywords</label>
                      <div className="keywords-pill-container">
                        {seoMetaKeywords.map((tag, idx) => (
                          <span key={idx} className="keyword-pill">
                            {tag}
                            <button type="button" className="keyword-pill-remove" onClick={() => handleRemoveKeyword(idx)}>
                              <FaTimes size={10} />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          id="seoMetaKeywords"
                          className="keywords-pills-input"
                          placeholder={seoMetaKeywords.length === 0 ? 'Type keyword and press Enter...' : 'Add keyword...'}
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyDown={handleKeywordKeyDown}
                        />
                      </div>
                      <span className="char-counter" style={{ textAlign: 'left', display: 'block' }}>
                        Type a keyword and press Enter or Comma (,) to add. Click &quot;x&quot; to remove.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Global Footer Buttons */}
            <div className="global-editor-footer">
              <button type="button" className="btn-premium-secondary" onClick={() => setView('list')}>
                Cancel
              </button>
              <button type="submit" className="btn-premium-primary">
                Save
              </button>
            </div>
          </form>
        )}
      </div>

      {/* CREATE MAIN CATEGORY MODAL */}
      {showMainModal && (
        <div className="premium-modal-overlay">
          <form onSubmit={handleCreateMain} className="premium-modal-wrapper">
            <div className="premium-modal-header">
              <h3>Create Category</h3>
              <button type="button" className="btn-close-modal" onClick={() => setShowMainModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="premium-modal-body">
              <div className="premium-form-group">
                <label htmlFor="mainTitle">
                  Category Title <span>*</span>
                </label>
                <input
                  type="text"
                  id="mainTitle"
                  className="premium-input"
                  placeholder="Category Title"
                  required
                  value={mainTitle}
                  onChange={(e) => setMainTitle(e.target.value)}
                />
              </div>
              <div className="premium-form-group">
                <label htmlFor="mainDesc">
                  Description <span>*</span>
                </label>
                <div className="quill-editor-wrapper">
                  <ReactQuill
                    id="mainDesc"
                    theme="snow"
                    value={mainDescription}
                    onChange={setMainDescription}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write description..."
                  />
                </div>
              </div>
            </div>
            <div className="premium-modal-footer">
              <button type="button" className="btn-premium-secondary" onClick={() => setShowMainModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-premium-primary">
                Submit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* CREATE SUB CATEGORY MODAL */}
      {showSubModal && (
        <div className="premium-modal-overlay">
          <form onSubmit={handleCreateSub} className="premium-modal-wrapper">
            <div className="premium-modal-header">
              <h3>Create Sub Category</h3>
              <button type="button" className="btn-close-modal" onClick={() => setShowSubModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="premium-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="premium-form-group">
                  <label htmlFor="subParent">
                    Category <span>*</span>
                  </label>
                  <select
                    id="subParent"
                    className="premium-select"
                    required
                    value={subParentId}
                    onChange={(e) => setSubParentId(e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {mainCategoriesList.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="premium-form-group">
                  <label htmlFor="subCode">
                    Category Code <span>*</span>
                  </label>
                  <input
                    type="text"
                    id="subCode"
                    className="premium-input"
                    placeholder="e.g. SWR"
                    required
                    value={subCode}
                    onChange={(e) => setSubCode(e.target.value)}
                  />
                </div>
              </div>

              <div className="premium-form-group">
                <label htmlFor="subTitle">
                  Category Title <span>*</span>
                </label>
                <input
                  type="text"
                  id="subTitle"
                  className="premium-input"
                  placeholder="Sub Category Title"
                  required
                  value={subTitle}
                  onChange={(e) => setSubTitle(e.target.value)}
                />
              </div>

              <div className="premium-form-group">
                <label htmlFor="subDesc">
                  Description <span>*</span>
                </label>
                <div className="quill-editor-wrapper">
                  <ReactQuill
                    id="subDesc"
                    theme="snow"
                    value={subDescription}
                    onChange={setSubDescription}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write description..."
                  />
                </div>
              </div>

              <div className="premium-form-group">
                <span
                  className="premium-label-title"
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'rgba(245, 240, 232, 0.75)',
                    marginBottom: '8px',
                    letterSpacing: '0.3px'
                  }}
                >
                  Show products under category listing page?
                </span>
                <div className="radio-group">
                  <label className="radio-option" htmlFor="showProductsYes">
                    <input
                      id="showProductsYes"
                      type="radio"
                      name="showProducts"
                      checked={subShowProducts === true}
                      onChange={() => setSubShowProducts(true)}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="radio-option" htmlFor="showProductsNo">
                    <input
                      id="showProductsNo"
                      type="radio"
                      name="showProducts"
                      checked={subShowProducts === false}
                      onChange={() => setSubShowProducts(false)}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="premium-modal-footer">
              <button type="button" className="btn-premium-secondary" onClick={() => setShowSubModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-premium-primary">
                Submit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* EDIT CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={!!categoryToEdit}
        type="edit"
        title="Modify Category"
        itemName={categoryToEdit?.title}
        itemType="category"
        onConfirm={handleConfirmEdit}
        onCancel={() => setCategoryToEdit(null)}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={showDeleteModal && !!categoryToDelete}
        type="delete"
        title="Delete Category"
        itemName={categoryToDelete?.title}
        itemType="category"
        onConfirm={handleDeleteCategory}
        onCancel={() => {
          setShowDeleteModal(false);
          setCategoryToDelete(null);
        }}
      />
    </>
  );
};

export default Categories;
